import { YeneShopLogo } from '@shared/ui/YeneShopLogo/YeneShopLogo';
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode, type MouseEvent } from 'react';
import { X } from '@phosphor-icons/react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useGetMeQuery } from '@entities/user';
import { Button } from '@shared/ui/Button';
import { Screen } from '@shared/ui/Screen';
import { SignInOptions, RETURN_TO_KEY } from './SignInOptions';
import styles from './SignInDialog.module.css';

type Request = { message?: string; returnTo?: string; onClose?: () => void };
const Context = createContext<{ authenticated: boolean; requireSignIn: (request?: Request) => boolean } | null>(null);
export const protectedPath = (path: string) => /^\/(notifications|account|orders|wallet|seller|products|panel|reseller|services|numbers|invite)(\/|$)/.test(path);
export function useSignIn() { const value = useContext(Context); if (!value) throw new Error('SignInProvider is missing'); return value; }
export function SignInProvider({ children }: { children: ReactNode }) {
  const { data: me, error, refetch } = useGetMeQuery();
  const authenticated = Boolean(me) && !(error && 'status' in error && error.status === 401);
  const location = useLocation(); const navigate = useNavigate();
  const [request, setRequest] = useState<Request | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const returnTo = location.pathname + location.search + location.hash;
  const requireSignIn = useCallback((next: Request = {}) => {
    if (authenticated) return true;
    setRequest({ ...next, returnTo: next.returnTo ?? returnTo }); return false;
  }, [authenticated, returnTo]);
  const close = () => { setRequest(null); request?.onClose?.(); };
  useEffect(() => {
    if (!request) return;
    const element = dialog.current; const previous = document.activeElement as HTMLElement | null;
    element?.showModal(); const overflow = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { element?.close(); document.body.style.overflow = overflow; previous?.focus(); };
  }, [request]);
  useEffect(() => {
    if (authenticated) setRequest(null);
  }, [authenticated]);
  useEffect(() => {
    if (!authenticated || !new URLSearchParams(location.search).has('connected')) return;
    try {
      const stored = sessionStorage.getItem(RETURN_TO_KEY); sessionStorage.removeItem(RETURN_TO_KEY);
      if (!stored) return;
      const saved = JSON.parse(stored);
      if (typeof saved.path !== 'string' || typeof saved.at !== 'number' || Date.now() - saved.at > 15 * 60_000) return;
      const destination = new URL(saved.path, window.location.origin);
      if (destination.origin === window.location.origin && destination.pathname !== '/login') navigate(destination.pathname + destination.search + destination.hash, { replace: true });
    } catch { /* A blocked or expired storage entry does not prevent sign-in. */ }
  }, [authenticated, location.search, navigate]);
  useEffect(() => {
    const expired = () => { void refetch(); setRequest({ message: 'Your session has expired. Sign in to continue.', returnTo }); };
    window.addEventListener('yeneshop:sign-in-required', expired);
    return () => window.removeEventListener('yeneshop:sign-in-required', expired);
  }, [refetch, returnTo]);
  function interceptLink(event: MouseEvent) {
    if (authenticated || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = (event.target as Element).closest<HTMLAnchorElement>('a[href]');
    if (!link || link.target === '_blank' || link.hasAttribute('download')) return;
    const destination = new URL(link.href);
    if (destination.origin !== window.location.origin || !protectedPath(destination.pathname)) return;
    event.preventDefault(); event.stopPropagation();
    requireSignIn({ returnTo: destination.pathname + destination.search + destination.hash });
  }
  return <Context.Provider value={{ authenticated, requireSignIn }}><div onClickCapture={interceptLink}>{children}</div>
    {request && <dialog ref={dialog} className={styles.dialog} aria-labelledby="sign-in-title" aria-describedby="sign-in-description" onCancel={event => { event.preventDefault(); close(); }} onClick={event => { if (event.target === event.currentTarget) close(); }}><div className={styles.body}>
      <header className={styles.header}><YeneShopLogo size={34} /><Button variant="ghost" aria-label="Close sign-in" onClick={close}><X size={22} /></Button></header>
      <div><h2 id="sign-in-title" className={styles.title}>Sign in to continue</h2><p id="sign-in-description" className={styles.description}>{request.message ?? 'Keep your purchases, orders and shop together in one account.'}</p></div>
      <SignInOptions returnTo={request.returnTo ?? returnTo} />
      <p className={styles.note}>Already shop with us in Telegram? Continue with Telegram to access your existing purchases.</p>
      <Button variant="ghost" fullWidth onClick={close}>Continue browsing</Button>
    </div></dialog>}
  </Context.Provider>;
}
export function RequireSignIn() {
  const { authenticated, requireSignIn } = useSignIn(); const navigate = useNavigate();
  useEffect(() => { if (!authenticated) requireSignIn({ onClose: () => navigate('/', { replace: true }) }); }, [authenticated, requireSignIn, navigate]);
  return authenticated ? <Outlet /> : <Screen title="Sign in"><Button onClick={() => requireSignIn({ onClose: () => navigate('/', { replace: true }) })}>Sign in to continue</Button></Screen>;
}
